<template>
  <div class="multipleRequirementsSelector">
    <v-sheet
        class="requirementSelection"
        elevation="5"
    >
      <v-slide-group
          v-model="requirementIndex"
          active-class="active"
          center-active
          show-arrows
          ref="requirements"
      >
        <v-slide-item
            v-for="{requirement, index} in visibleRequirements"
            :key="index"
            :value="index"
            v-slot="{ active, toggle }"
        >
          <v-sheet
              class="secondary requirement"
              :class="{active: active}"
              @click="selectRequirement(index)"
          >
            <span class="requirementLabel">{{ requirement.productName }}</span>
            <div class="selectedImageContainer">
              <div v-if="requirementNeedsAllImagesOfPerson(requirement.requirement)" class="allImagesIconContainer">
                <v-badge overlap bottom color="red" class="badge">
                  <template v-slot:badge color="white">{{ getImages(requirement.requirement).length }}</template>
                  <v-icon class="secondary--text" :size="$vuetify.breakpoint.xsOnly ? 40 : 70">photo_library</v-icon>
                </v-badge>
              </div>
              <ManuallyExpandableImagePreview v-else-if="value[index] && value[index][0]"
                                              class="selectedImage"
                                              :filter="requirements[index].productFilter"
                                              :small-height="getPreviewSize(index).height"
                                              :small-width="getPreviewSize(index).width"
                                              :image="getPreviewImage(index)"/>
              <img v-else-if="requirement.productImageUrl" :src="requirement.productImageUrl"
                   class="requirementProductImage">
              <v-icon v-else :size="$vuetify.breakpoint.xsOnly ? 40 : 70" class="addImageIcon">library_add</v-icon>
            </div>
          </v-sheet>
        </v-slide-item>
      </v-slide-group>
    </v-sheet>
    <div class="secondary imageSelectorContainer">
      <ImageSelector :value="value[requirementIndex][0]"
                     :key="person.code + ':' + requirementIndex"
                     :images="getImages(currentRequirement.requirement)"
                     :excluded-default-image-ids="excludedDefaultImageIds"
                     :productId="currentRequirement.productId"
                     :filter="currentRequirement.productFilter"
                     :select-all="currentRequirementNeedsAllImagesOfPerson"
                     :scrollable-container-parent-layers="2"
                     ref="imageSelector"
                     @input="onImageSelect(requirementIndex, ...arguments)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {Prop} from "vue-property-decorator";
import {ConfiguredImageDto, DetailedImageRequirementDto, ImageDto, ImageOrientationDto, ImageRequirementDto, PersonDto, ProductMediumDto} from "@swagger";
import ImageSelector from "@/components/product-configuration/ImageSelector.vue";
import {ImageRows} from "@/global/types";
import ImagePreview from "@/components/ImagePreview.vue";
import ManuallyExpandableImagePreview from "@/components/ManuallyExpandableImagePreview.vue";
import TypeEnum = ImageRequirementDto.TypeEnum;
import Vue from "vue";

@Component({
  components: {ManuallyExpandableImagePreview, ImagePreview, ImageSelector}
})
export default class MultipleRequirementsSelector extends BasicComponent {

  @Prop(Array) value: ConfiguredImageDto[][];
  @Prop(Array) requirements: DetailedImageRequirementDto[];
  @Prop(Boolean) detailedConfiguration: boolean;
  @Prop(Object) person: PersonDto;
  @Prop(Boolean) preservePrefilledChoices: boolean;

  requirementIndex: number = 0; // The requirement which is currently being configured

  created() {
    if (this.preservePrefilledChoices) this.requirementIndex = this.firstUnsatisfiedRequirementIndex ?? 0;
  }

  mounted() {
    setTimeout(() => { // Step expanded
      const imageSelector = this.$refs.imageSelector as ImageSelector;
      imageSelector.onContainerSizeChange();
    }, 1000);
  }

  getImages(requirement: ImageRequirementDto): ImageRows {
    const imageIds = new Set(this.person.imageIds);
    const requirements = !this.detailedConfiguration && this.requirementNeedsAllImagesOfPerson(requirement)
        ? this.requirements.filter(r => this.requirementNeedsAllImagesOfPerson(r.requirement)).map(r => r.requirement)
        : [requirement];
    return this.$store.getters.imageSelection.images.filter((image: ImageDto) => imageIds.has(image.uniqueId) &&
        requirements.some(r => this.imageMatchesRequirement(image, r)));
  }

  get visibleRequirements() {
    const firstDownload = this.requirements.findIndex(r => this.requirementNeedsAllImagesOfPerson(r.requirement));
    return this.requirements.map((requirement, index) => ({requirement, index})).filter(({requirement, index}) =>
        this.detailedConfiguration || !this.requirementNeedsAllImagesOfPerson(requirement.requirement) || index === firstDownload);
  }

  selectRequirement(index: number): void {
    this.requirementIndex = index;
  }

  onImageSelect(requirementIndex: number, images: ConfiguredImageDto | ConfiguredImageDto[]) {
    if (this.requirements[requirementIndex].requirement.type === TypeEnum.ALLOFPERSON) {
      if (!this.detailedConfiguration) {
        this.requirements.forEach((requirement, index) => {
          if (!this.requirementNeedsAllImagesOfPerson(requirement.requirement)) return;
          this.$set(this.value, index, (images as ConfiguredImageDto[])
              .filter(image => this.imageMatchesRequirement(this.$store.getters.imageById(image.uniqueImageId), requirement.requirement))
              .map(image => {
                const previous = this.preservePrefilledChoices
                    ? this.value[index].find(choice => choice.uniqueImageId === image.uniqueImageId) : undefined;
                return {...image, ...previous, productId: requirement.productId,
                  filter: requirement.productFilter ?? previous?.filter};
              }));
        });
        return;
      }
      const previous = this.value[requirementIndex];
      const selected = this.preservePrefilledChoices && Array.isArray(images)
        ? images.map(image => previous.find(choice => choice.uniqueImageId === image.uniqueImageId) ?? image) : images;
      this.$set(this.value, requirementIndex, selected);
      return;
    }
    this.$set(this.value, requirementIndex, [images]);
  }

  getPreviewImage(index: number): ImageDto {
    const image = this.value[index][0];
    return this.$store.getters.imageById(image.uniqueImageId);
  }

  getPreviewSize(index: number): {height?: string; width?: string} {
    if (!this.$vuetify.breakpoint.xsOnly) {
      return {};
    }
    const landscape = this.getPreviewImage(index)?.orientation === ImageOrientationDto.LANDSCAPE;
    return landscape ? {height: "46px", width: "64px"} : {height: "64px", width: "46px"};
  }

  navigateToStep(index: number): void {
    this.requirementIndex = index;
    this.scrollToRequirements();
  }

  scrollToRequirements(): void {
    const ref = this.$refs["requirements"] as Vue;
    if (!ref) {
      return;
    }
    ref.$el.scrollIntoView({behavior: "smooth"});
  }

  navigateToNextStep(): void {
    if (this.configurationComplete) {
      return;
    }
    this.navigateToStep(this.nextStepIndex);
  }

  reset(): void {
    this.requirementIndex = 0;
  }

  get currentRequirement(): DetailedImageRequirementDto {
    return this.requirements[this.requirementIndex];
  }

  get excludedDefaultImageIds(): string[] {
    if (this.currentRequirement.productMedium !== ProductMediumDto.DOWNLOAD) {
      return [];
    }
    return this.requirements.flatMap((requirement, index) => index !== this.requirementIndex
      && requirement.productMedium === ProductMediumDto.DOWNLOAD
      && !this.requirementNeedsAllImagesOfPerson(requirement.requirement)
        ? this.value[index].map(image => image.uniqueImageId) : []);
  }

  get firstUnsatisfiedRequirementIndex(): number {
    for (let i = 0; i < this.requirements.length; i++) {
      if (!this.isRequirementSatisfied(i)) {
        return i;
      }
    }
    return null;
  }

  get nextStepIndex(): number {
    if (this.requirementIndex < this.requirements.length - 1
        && this.requirements[this.requirementIndex + 1].requirement.type === TypeEnum.ALLOFPERSON) {
      return this.requirementIndex + 1;
    }
    return this.firstUnsatisfiedRequirementIndex;
  }

  get configurationComplete(): boolean {
    return this.firstUnsatisfiedRequirementIndex == null;
  }

  get currentRequirementSatisfied(): boolean {
    return this.isRequirementSatisfied(this.requirementIndex);
  }

  get currentRequirementNeedsAllImagesOfPerson(): boolean {
    return this.requirementNeedsAllImagesOfPerson(this.currentRequirement.requirement);
  }

  requirementNeedsAllImagesOfPerson(requirement: ImageRequirementDto): boolean {
    return requirement.type === TypeEnum.ALLOFPERSON;
  }

  isRequirementSatisfied(index: number): boolean {
    return this.value[index].length > 0;
  }

}

</script>
<style lang="scss" scoped>
@use "../../stylesheets/Variables.scss" as *;

$border-width: 2px;
$border-color-unselected: $shop-divider;
$border-color-selected: var(--v-accent-base);
$border-radius: 10px;

.multipleRequirementsSelector {
  position: relative;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: scroll;

  .requirement {
    $height: 180px;
    $shadowHeight: 2px;
    $padding: 15px;
    display: flex;
    flex-direction: column;
    height: calc(#{$height});
    margin-top: 10px;
    width: auto;
    min-width: calc(128px + 2 * #{$padding});
    padding: $padding;
    transform: none;
    border-width: 2px;
    border-style: solid;
    border-top-left-radius: $border-radius;
    border-top-right-radius: $border-radius;
    cursor: pointer;
    border-collapse: collapse;
    background-color: $shop-white !important;

    &:not(.active) {
      border-top-color: $border-color-unselected !important;
      border-left-color: $border-color-unselected !important;
      border-right-color: $border-color-unselected !important;
      //border-bottom-color: $border-color-selected;
    }

    &.active {
      height: $height;

      border-bottom-width: 0;
      transform: none;
      z-index: 4;
      //border-color: $border-color-selected;
      border-bottom-color: $shop-white;

      &::after {
        width: 100%;
        height: 4px;
        background-color: $shop-white;
        display: block;
        bottom: -4px;
        content: " ";
      }
    }

    .requirementLabel {
      display: block;
      width: 100%;
      text-align: center;
      font-weight: 500;
    }

    .selectedImageContainer {
      flex: 1 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;

      .selectedImage, .requirementProductImage {
      }

      .requirementProductImage {
        max-width: 100%;
        max-height: 128px;
      }

      .addImageIcon {
      }

      .allImagesIconContainer {
        position: relative;
        height: 100%;

        .badge {

        }
      }
    }

    @media screen and (max-width: 599px) {
      &, &.active {
        height: auto;
        min-height: 112px;
        min-width: 128px;
        padding: 8px;
        margin-top: 4px;
      }

      .requirementLabel {
        font-size: 14px;
        line-height: 20px;
      }

      .selectedImageContainer .requirementProductImage {
        max-height: 64px;
      }
    }

  }

  .imageSelectorContainer {
    //border-radius: $border-radius;
    border-top-left-radius: 0;
    border-width: $border-width;
    border-style: solid;
    margin-top: calc(-#{$border-width});
    margin-bottom: 5px;
    overflow: hidden;
    background-color: $shop-white !important;
  }

}
</style>
<style lang="scss">

.multipleRequirementsSelector {
  .imageSelector {
    .imageSheet {
      box-shadow: none !important;
    }
  }

  $arrow-width: 40px;

  .v-slide-group__prev {
    min-width: $arrow-width;
    margin-left: -12px;
  }

  .v-slide-group__next {
    min-width: $arrow-width;
    margin-right: -12px;
  }

  .v-slide-group__prev--disabled, .v-slide-group__next--disabled {
    display: none;
  }

  @media screen and (max-width: 599px) {
    .v-slide-group__prev, .v-slide-group__next {
      align-self: flex-start;
      min-width: 32px;
      height: 44px;
      margin: 4px 0 0;
    }
  }
}

</style>
